/* eslint-disable i18next/no-literal-string */
import { pageDataService } from 'common/utilities';
import { Platform } from 'mcsclient/models';

import { BaseTheme } from '@suiteux/suiteux-shell-typings/typings/suiteux-shell';

import { AppSemanticColors, createAppSemanticColors, CustomSemanticColors, staticAppColors } from './AppSemanticColors';
import { CreateCustomTheme } from './Common';
import { FluentEffects, FluentPalette, FluentSemanticColors } from './ThemeTypes';

const isWebView = pageDataService.isWebView();

const fluentPalette: FluentPalette = {
  // theme colors
  themePrimary: staticAppColors.bluePrimary,
  themeLighterAlt: staticAppColors.neutralGray0,
  themeLighter: staticAppColors.anchorShade40,
  themeLight: staticAppColors.anchorShade20,
  themeTertiary: staticAppColors.blueShade30,
  themeSecondary: staticAppColors.blueShade10,
  themeDarkAlt: staticAppColors.blueTint10,
  themeDark: staticAppColors.blueTint15,
  themeDarker: staticAppColors.blueTint20,
  // neutrals
  neutralLighterAlt: staticAppColors.anchorShade30,
  neutralLighter: staticAppColors.anchorShade10,
  neutralLight: staticAppColors.neutralGray26,
  neutralQuaternaryAlt: staticAppColors.neutralGray38,
  neutralQuaternary: staticAppColors.neutralGray74,
  neutralTertiaryAlt: staticAppColors.neutralGray82,
  neutralTertiary: staticAppColors.neutralGray94,
  neutralSecondary: staticAppColors.anchorTint55,
  neutralPrimaryAlt: staticAppColors.anchorTint60,
  neutralPrimary: staticAppColors.white100,
  neutralDark: staticAppColors.neutralGray98,
  // standard colors
  black: staticAppColors.white100,
  blackTranslucent40: staticAppColors.white40,
  white: staticAppColors.anchorShade30,
  whiteTranslucent40: staticAppColors.anchorShade30Transparent40,
  green: staticAppColors.successPrimary,
};

const fluentSemanticColors: FluentSemanticColors = {
  bodyText: staticAppColors.white100,
  bodySubtext: staticAppColors.white100,
  errorText: staticAppColors.criticalPrimary,
  cardShadow: 'rgba(179, 191, 235, 1)', // TODO
  cardShadowHovered: 'rgba(179, 191, 235, 0.7)', // TODO
  // button
  buttonText: staticAppColors.white100,
  buttonTextHovered: fluentPalette.themeDarkAlt,
  buttonTextPressed: fluentPalette.themeSecondary,
  buttonTextDisabled: staticAppColors.anchorTint20,
  buttonBackground: 'transparent',
  buttonBorder: staticAppColors.white100,
  // primary button
  primaryButtonBackground: staticAppColors.bluePrimary,
  primaryButtonBackgroundDisabled: staticAppColors.anchorShade20,
  primaryButtonBackgroundHovered: fluentPalette.themeDarkAlt,
  primaryButtonBackgroundPressed: fluentPalette.themeSecondary,
  primaryButtonBorder: fluentPalette.themePrimary,
  primaryButtonText: staticAppColors.white100,
  primaryButtonTextDisabled: staticAppColors.anchorTint20,
  primaryButtonTextHovered: staticAppColors.white100,
  primaryButtonTextPressed: staticAppColors.white100,
  // link
  link: fluentPalette.themeDark,
  linkHovered: fluentPalette.themeDarkAlt,
};

const customAppColors: CustomSemanticColors = {
  // whites
  white100ToAnchor20: staticAppColors.anchorShade20,
  white90ToAnchor30tr90: staticAppColors.anchorShade30Transparent90,
  white60ToAnchor30tr60: staticAppColors.anchorShade30Transparent60,
  white60ToAnchor20tr60: staticAppColors.anchorShade20Transparent60,
  white60ToAnchor20: staticAppColors.anchorShade20,
  white60ToAnchor10: staticAppColors.anchorShade10,
  white40ToBlack40: 'rgba(0, 0, 0, 0.4)',
  white40ToAnchor30tr40: staticAppColors.anchorShade30Transparent40,
  white40ToAnchor20tr60: staticAppColors.anchorShade20Transparent60,
  white30ToAnchor20tr60: staticAppColors.anchorShade20Transparent60,
  // reds (critical colors)
  redPrimaryToRed10: staticAppColors.criticalRed10,
  redPrimaryToCrimson70: staticAppColors.crimson70,
  red600ToRedPrimary: staticAppColors.criticalPrimary,
  red600ToCrimson70: staticAppColors.crimson70,
  red20ToRedPrimary: staticAppColors.criticalPrimary,
  red20ToCrimson70: staticAppColors.crimson70,
  red20ToRedTint10: staticAppColors.criticalRedTint10,
  red20ToWhite: staticAppColors.white100,
  redTint50ToRed30: staticAppColors.criticalRed30,
  // greens (success colors)
  green20ToGreenPrimary: staticAppColors.successPrimary,
  greenBackgroundToSeafoam30: staticAppColors.seafoam30,
  greenBackgroundToSeafoam30RGBA0: staticAppColors.seafoam30RGBA0,
  greenBackgroundToSeafoam30RGBA100: staticAppColors.seafoam30RGBA60,
  // grays (neutral colors)
  gray98ToAnchor30: staticAppColors.anchorShade30,
  gray98ToAnchor20tr60: staticAppColors.anchorShade20Transparent60,
  gray94ToAnchor40: staticAppColors.anchorShade40,
  gray94ToAnchor20: staticAppColors.anchorShade20,
  gray94ToAnchor10: staticAppColors.anchorShade10,
  gray94ToTransparent: staticAppColors.transparent,
  gray74ToAnchorTint20: staticAppColors.anchorTint20,
  gray38ToWhite: staticAppColors.white100,
  gray26ToGray94: staticAppColors.neutralGray94,
  gray14ToWhite: staticAppColors.white100,
  // blues
  bluePrimaryToWhite: staticAppColors.white100,
  blue20ToBluePrimary: staticAppColors.bluePrimary,
  blue20ToBlueTint10: staticAppColors.blueTint10,
  blue20ToWhite: staticAppColors.white100,
  blueTint40ToBlue10: staticAppColors.blueShade10,
  blueTint20ToBlue20: staticAppColors.blueShade20,
  anchorTint60ToAnchor30: staticAppColors.anchorShade30,
  anchorTint55ToAnchor40: staticAppColors.anchorShade40,
  // other
  neutralPrimaryToWhite: staticAppColors.white100,

  // component-specific colors
  text: staticAppColors.white100,
  textLight: staticAppColors.white100,
  lightHeadlineText: staticAppColors.white100,
  actionButtonText: staticAppColors.blueTint15,
  pageBackground: isWebView ? 'transparent !important' : `${staticAppColors.anchorShade40} !important`,
  pageBackgroundColor: staticAppColors.anchorShade40,
  pageFooterBackground: staticAppColors.anchorShade30Transparent40,
  copilotPanelBackground: staticAppColors.anchorShade30,
  calloutBackground: fluentSemanticColors.bodyText,
  horizontalLineColor: '#CCD9E4', // TODO
  inputBorderUnfocused: staticAppColors.neutralGray74,
  completionCountBackground: staticAppColors.anchorShade20,
  criticalRedBadge: staticAppColors.criticalRed10,
  deviceAlertTimelineFill: staticAppColors.anchorShade20,
  // cards
  cardContentBackground: staticAppColors.anchorShade30,
  cardContentBackgroundHovered: '#F0F4FF', // TODO
  cardBorder: '#DCDCDC', // TODO
  cardBorderFocused: '#A7AECC', // TODO
  // dialog
  dialogBackground: staticAppColors.anchorShade30,
  dialogBackgroundColor: staticAppColors.anchorShade30,
  dialogBackdrop: isWebView ? 'transparent' : 'rgba(0, 0, 0, 0.6)',
  dialogOverlayBackground: 'transparent',
  dialogFooterBackground: staticAppColors.anchorShade30Transparent60,
  dialogCloseIcon: staticAppColors.white100,
  dialogCloseIconHover: staticAppColors.neutralGray82,
  // list/table
  listBorder: '#E1E1E1', // TODO
  listBorderFocused: '#344554', // TODO
  listHeaderBackground: staticAppColors.anchorShade30Transparent60,
  listHeaderText: staticAppColors.white100,
  listFooterBackground: staticAppColors.anchorShade30Transparent40,
  listZeroDataBackground: staticAppColors.anchorShade30Transparent60,
  // tags
  greenTagBackground: staticAppColors.blueShade40,
  yellowTagBackground: staticAppColors.neutralGray14,
  redTagBackground: staticAppColors.criticalRed40,
  defaultTagBackground: staticAppColors.anchorShade20,
  redTagText: staticAppColors.criticalRedTint10,
  greenTagBorder: staticAppColors.blueShade30,
  redTagBorder: staticAppColors.criticalRed20,
  // icons
  themeIconDefault: staticAppColors.white100,
  iconCircleBackground: staticAppColors.anchorShade10,
  // scrollbar
  customScrollbarThumb: staticAppColors.anchorShade20Transparent90,
  customScrollbarThumbHover: staticAppColors.anchorShade10,
};

const effects: FluentEffects = {
  elevation2: '0px 1px 2px rgba(0, 0, 0, 0.28), 0px 0px 2px rgba(0, 0, 0, 0.24)',
  elevation4: '0px 2px 4px rgba(0, 0, 0, 0.28), 0px 0px 2px rgba(0, 0, 0, 0.24)',
  elevation8: '0px 4px 8px rgba(0, 0, 0, 0.28), 0px 0px 2px rgba(0, 0, 0, 0.24)',
  elevation16: '0px 8px 16px rgba(0, 0, 0, 0.28), 0px 0px 2px rgba(0, 0, 0, 0.24)',
  elevation28: '0px 14px 28.8px rgba(0, 0, 0, 0.48), 0px 0px 8px rgba(0, 0, 0, 0.4)',
  elevation64: '0px 32px 64px rgba(0, 0, 0, 0.48), 0px 0px 8px rgba(0, 0, 0, 0.4)',
};

const appSemanticColors: AppSemanticColors = createAppSemanticColors(customAppColors);

export const DarkTheme = (webViewPlatform: Platform | undefined) =>
  CreateCustomTheme(webViewPlatform, fluentPalette, fluentSemanticColors, appSemanticColors, effects);

export const DarkShellAppBrandTheme: BaseTheme = {
  Primary: 'rgba(255, 255, 255, 0)',
  Accent: '#4599FF',
  AppName: 'rgba(255, 255, 255, 1)',
  DefaultText: 'rgba(255, 255, 255, 1) !important',
  HoverText: 'rgba(255, 255, 255, 1)',
  HoverBackground: '#3E4175',
  PressedText: 'rgba(255, 255, 255, 1)',
  PressedBackground: '#383A69',
  SelectedText: 'rgba(255, 255, 255, 1)',
  SelectedBackground: '#242641',
};
